import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useRuntimeConfig } from '@/config/useRuntimeConfig';
import { useAuth } from '@/auth/useAuth';
import { useCan } from '@/auth/consoleCapabilityContext';
import { fetchAccounts } from '@/api/accounts';
import { accountDisplayName } from '@/pages/Console/Accounts/Profile/accountDisplayName';

/** Server ceiling (`emg_shared.api.common.paginate_queryset`), not a preference. */
const REALM_PAGE_SIZE = 100;

/**
 * EAA-1839 (task 4 fix): same cap and the same reasoning as
 * `useScopedAccounts.ts`'s `MAX_SELECT_ALL_PAGES` — the realm list has no
 * "give me everyone" mode, only 100-row pages, so a complete join means
 * walking pages, and the walk needs a stop condition so a very large realm
 * cannot hang this join forever. Kept in sync with that constant's value
 * (20 × 100 = 2,000 accounts) rather than inventing a different number for
 * what is fundamentally the same kind of walk.
 */
const MAX_WALK_PAGES = 20;

export interface ScopedAccountUsernamesResult {
    /** `user_sub` → realm `username`, for every account the walk reached. */
    usernameBySub: Map<string, string>;
    /**
     * EAA-1839 (2026-08-24, user request: "show first name last name on top,
     * username below — if not have, only username"): `user_sub` →
     * `accountDisplayName({first_name, last_name, username})`, computed from the
     * SAME walked realm rows `usernameBySub` comes from — first/last name cost
     * nothing extra to capture, they were already on every row and simply not
     * read before this.
     *
     * CORRECTS a false claim this board carried until this pass: a comment on
     * `identityPrimaryOf` asserted "Keycloak simply has no names set on these
     * accounts in this realm", based on opening ONE account's profile modal
     * (`gvanhdo`, which genuinely has neither). The realm walk's own response
     * carries `first_name`/`last_name` populated on MANY other rows in the same
     * realm (verified live: e.g. "Thinh Nguyen", "Bạch Vũ") — the true
     * constraint was never "no names exist", only that a per-row
     * `GET /accounts/{sub}` to fetch them one at a time was too expensive
     * (20 requests per page render). This map is the SAME one-request-per-walk
     * cost `usernameBySub` already pays, just reading two more fields off rows
     * already in hand.
     *
     * Resolves to the username alone when Keycloak has no name for that sub —
     * `accountDisplayName`'s own fallback — so a caller comparing this against
     * `usernameBySub`'s entry can tell "a real name" from "just the username"
     * by equality, exactly how the realm board's own identity cell does.
     */
    displayNameBySub: Map<string, string>;
    /**
     * EAA-1839: `user_sub` → realm `email`, from the SAME walk — the realm rows
     * this hook already fetches carry an email that used to be read and thrown
     * away, so this map costs nothing extra.
     *
     * WHY IT IS NEEDED. `Assignment.user_email` is a DENORMALIZED snapshot the
     * backend writes at grant time (`blank=True, default=""`), not a live lookup,
     * and `AssignmentService.bulk_apply` creates rows without it — it is handed
     * only `user_subs`, so it has no address to store, and its
     * revoke-then-recreate branch drops the one the previous row held. Verified
     * live: one account held five assignments, three with an address and the two
     * most recent with `''`, so the SAME person rendered "(chưa có email)" on one
     * row and a real address on another. Keycloak is the source of truth for an
     * address; when the snapshot is blank this map supplies it.
     *
     * This does NOT paper over the backend defect — a blank snapshot still breaks
     * `GET /admin/apps/{code}/accounts?search=`, which matches `user_email`
     * server-side and cannot see this map. The fix belongs in `bulk_apply`; this
     * stops the console from showing one person two different ways meanwhile.
     */
    emailBySub: Map<string, string>;
    /**
     * EAA-1839: true when the walk hit `MAX_WALK_PAGES` before exhausting the
     * realm list — the map above is a PREFIX, not a complete join, and some
     * rows with a real username will still show none. Callers must surface
     * this, not just silently degrade (see `ScopedAccountsTable`'s capped
     * `Callout`) — a missing username that reads as "no username" when it
     * actually means "we stopped looking" is the exact failure this fixes.
     */
    capped: boolean;
}

/**
 * EAA-1839 (frame 34 "Tài khoản" column, second line): a `sub → username`
 * lookup for the org-scoped accounts table.
 *
 * WHY THIS EXISTS: `GET /organizations/{id}/accounts` (`ScopedAccountRow` =
 * `OrgAssignment`) carries `user_sub` + `user_email` only — no Keycloak join,
 * confirmed in `api/orgAdmin.ts`'s contract note above `ScopedAccountRow`.
 * `username` lives ONLY on `KeycloakAccountRow`, from the realm list
 * `GET /api/v1/accounts` (`api/accounts.ts`). That endpoint is `IsSuperAdmin`
 * server-side, so:
 *   - the request is gated on `console.accounts.view` client-side (UX only —
 *     the server re-checks and would 403 an org-admin who lacks it), and
 *   - the realm list is walked ONCE per mount/session (cached by TanStack
 *     Query under a stable key, `staleTime` 5 minutes), never once per row
 *     and never once per scoped-accounts page turn. 20 rows × a per-row
 *     `GET /accounts` would be 20 requests per page render for a field this
 *     screen has never needed until now — see `ScopedAccountsTable`'s header
 *     comment for the cost this was weighed against (per-row
 *     `GET /accounts/{sub}` for first/last name, which this hook
 *     deliberately does NOT do).
 *
 * EAA-1839 (task 4 fix, 2026-08-21): this used to fetch a SINGLE
 * `page_size=100` realm page and call that the join. That is not a join, it
 * is a lookup against an arbitrary first 100 accounts — confirmed against a
 * real tenant where `vthl2002@gmail.com` (username `phuvanschool`) sat
 * outside that first page and silently fell back to showing its email, while
 * `gvanhdo`/`emgadmin`/`adanhdo` (inside the first page) resolved fine. The
 * fix walks every realm page up to `MAX_WALK_PAGES`, the same cap
 * `useScopedAccounts.ts`'s `MAX_SELECT_ALL_PAGES` uses for its own realm/org
 * walks, and reports whether it had to stop early via `capped` so a caller
 * can say so instead of the gap looking like "this account has no username".
 *
 * HONEST DEGRADE, not a placeholder: a sub that is not on any walked realm
 * page (or any sub, when the caller lacks `console.accounts.view`) simply has
 * no entry in the returned map. Callers must render nothing for that row's
 * username line rather than inventing one — see `ScopedAccountsTable`'s
 * `identitySecondaryOf`.
 */
export function useScopedAccountUsernames(): ScopedAccountUsernamesResult {
    const config = useRuntimeConfig();
    const { authenticated, accessToken } = useAuth();
    const can = useCan();

    const canReadRealm = can('console.accounts.view');
    const enabled = authenticated && Boolean(accessToken) && canReadRealm;

    const query = useQuery({
        queryKey: ['scoped-accounts-username-join-walk', accessToken],
        queryFn: async () => {
            const map = new Map<string, string>();
            const displayNames = new Map<string, string>();
            const emails = new Map<string, string>();
            let walkPage = 1;
            let capped = false;
            for (;;) {
                const res = await fetchAccounts(config.apiBase, accessToken as string, {
                    page: walkPage,
                    page_size: REALM_PAGE_SIZE,
                });
                for (const row of res.results) {
                    map.set(row.sub, row.username);
                    displayNames.set(
                        row.sub,
                        accountDisplayName({
                            first_name: row.first_name,
                            last_name: row.last_name,
                            username: row.username,
                            email: '',
                        })
                    );
                    // Only a non-empty address is recorded: an empty string here
                    // would shadow the assignment's own snapshot with a value that
                    // is no better, and callers distinguish "absent" from "blank".
                    if (row.email) emails.set(row.sub, row.email);
                }
                const reachedEnd = walkPage >= res.total_pages;
                if (reachedEnd) break;
                if (walkPage >= MAX_WALK_PAGES) {
                    capped = true;
                    break;
                }
                walkPage += 1;
            }
            return { map, displayNames, emails, capped };
        },
        enabled,
        // Usernames change rarely; this join is a display convenience, not a
        // source of truth, so a long staleTime avoids re-walking the realm
        // list on every scoped-accounts page turn.
        staleTime: 5 * 60 * 1000,
    });

    return useMemo(
        () => ({
            usernameBySub: query.data?.map ?? new Map<string, string>(),
            displayNameBySub: query.data?.displayNames ?? new Map<string, string>(),
            emailBySub: query.data?.emails ?? new Map<string, string>(),
            capped: Boolean(query.data?.capped),
        }),
        [query.data]
    );
}

export default useScopedAccountUsernames;
